export interface SpeedReportPayload {
  averageSpeed: number;
  overallSpeed: number;
  totalBytes: number;
  measurements: number;
  src?: string;
  quality: string;
}

export interface SpeedReportSnapshot {
  downloadSpeeds: number[];
  totalBytes: number;
  totalTimeMs: number;
  src?: string;
  quality: string;
}

function buildSpeedReportPayload(snapshot: SpeedReportSnapshot): SpeedReportPayload | null {
  if (snapshot.downloadSpeeds.length === 0) return null;

  return {
    averageSpeed: Math.round(getAverageSpeed(snapshot.downloadSpeeds)),
    overallSpeed: Math.round(getOverallSpeed(snapshot.totalBytes, snapshot.totalTimeMs)),
    totalBytes: Math.round(snapshot.totalBytes),
    measurements: snapshot.downloadSpeeds.length,
    src: snapshot.src,
    quality: snapshot.quality,
  };
}

export function startSpeedMonitorTimer(
  currentTimer: ReturnType<typeof setInterval> | null,
  intervalMs: number,
  getSnapshot: () => SpeedReportSnapshot,
  emitReport: (payload: SpeedReportPayload) => void,
): ReturnType<typeof setInterval> | null {
  if (currentTimer) return currentTimer;

  return setInterval(() => {
    const payload = buildSpeedReportPayload(getSnapshot());
    if (!payload) return;
    emitReport(payload);
  }, intervalMs);
}

export function stopSpeedMonitorTimer(
  currentTimer: ReturnType<typeof setInterval> | null,
): ReturnType<typeof setInterval> | null {
  if (currentTimer) {
    clearInterval(currentTimer);
  }
  return null;
}

export function getAverageSpeed(downloadSpeeds: number[]): number {
  if (downloadSpeeds.length === 0) return 0;
  const sum = downloadSpeeds.reduce((a, b) => a + b, 0);
  return sum / downloadSpeeds.length;
}

export function getOverallSpeed(totalBytes: number, totalTimeMs: number): number {
  if (totalTimeMs === 0) return 0;
  const bits = totalBytes * 8;
  const seconds = totalTimeMs / 1000;
  return bits / 1_000_000 / seconds;
}

export function accumulateSpeedSample(
  downloadSpeeds: number[],
  totalBytes: number,
  totalTimeMs: number,
  bytes: number,
  downloadTimeMs: number,
): { totalBytes: number; totalTimeMs: number } {
  const bits = bytes * 8;
  const seconds = downloadTimeMs / 1000;
  const mbps = bits / 1_000_000 / seconds;
  downloadSpeeds.push(mbps);

  return {
    totalBytes: totalBytes + bytes,
    totalTimeMs: totalTimeMs + downloadTimeMs,
  };
}
